/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { NotFoundError, throwAsServiceError } from "@/lib/queryErrors";
import { getAttributionForInsert } from "@/lib/attribution";
import { estimateLeadValue } from "@/lib/analytics";
import { bridgeLeadToGhl, trackLeadConversion } from "@/lib/lead-tracking";
import { MENU_PACKAGES_SELECT, toMenuPackages, type MenuPackage } from "@/lib/menu-packages";

export interface PublishedMenuRow {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  cover_image_url: string | null;
  og_image_url: string | null;
  show_prices: boolean;
  is_published: boolean;
  is_listed: boolean | null;
  cta_text: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  created_by_profile?: { display_name: string | null } | null;
  updated_by_profile?: { display_name: string | null } | null;
}

export interface MenuSectionRow {
  id: string;
  menu_id: string;
  title: string;
  description: string | null;
  sort_order: number;
  items?: MenuSectionItemRow[];
}

export interface MenuSectionItemRow {
  id: string;
  section_id: string;
  menu_item_id: string;
  sort_order: number;
  price_override: number | null;
  description_override: string | null;
  menu_item?: any;
}

// ─── List all published menus (admin) ───
export function usePublishedMenus() {
  return useQuery({
    queryKey: ["published_menus"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("published_menus")
        .select("*, created_by_profile:profiles!published_menus_created_by_fkey(display_name), updated_by_profile:profiles!published_menus_updated_by_fkey(display_name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as PublishedMenuRow[];
    },
  });
}

// ─── Get single menu with sections + items (admin) ───
export function usePublishedMenu(id: string | undefined) {
  return useQuery({
    queryKey: ["published_menus", id],
    enabled: !!id,
    queryFn: async () => {
      const { data: menu, error } = await (supabase as any)
        .from("published_menus")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;

      // fetchMenuPackageIds throws rather than degrading: the editor saves
      // attachments by delete + insert, so an empty list from a failed load
      // would wipe them on save (#932).
      const [{ data: sections, error: se }, package_ids] = await Promise.all([
        (supabase as any)
          .from("published_menu_sections")
          .select("*, items:published_menu_items(*, menu_item:menu_items(*, category:menu_categories(id,name), prices:menu_item_prices(*)))")
          .eq("menu_id", id)
          .order("sort_order"),
        fetchMenuPackageIds(id as string),
      ]);
      if (se) throw se;

      return { ...menu, package_ids, sections: (sections || []).map((s: any) => ({
        ...s,
        items: (s.items || []).sort((a: any, b: any) => a.sort_order - b.sort_order),
      })) } as PublishedMenuRow & { sections: MenuSectionRow[]; package_ids: string[] };
    },
  });
}

// ─── Get public menu by slug ───
// Query config is factored out so route loaders can kick off the same
// fetch in parallel with component code loading (issue #147). The hook
// below reuses the exact same queryKey so it hydrates from the loader.
export type PublicMenu = PublishedMenuRow & { sections: MenuSectionRow[]; packages: MenuPackage[] };

export const publicMenuQuery = (slug: string) => ({
  queryKey: ["public_menu", slug] as const,
  queryFn: async (): Promise<PublicMenu> => {
    // `.maybeSingle()` lets us distinguish the "no rows" case (bad slug)
    // from the "something blew up" case (network / 5xx). See issue #144.
    const { data: menu, error } = await (supabase as any)
      .from("published_menus")
      .select("*")
      .eq("slug", slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throwAsServiceError(error);
    if (!menu) throw new NotFoundError(`Menu "${slug}" not found`);

    const [{ data: sections, error: se }, { data: links, error: pe }] = await Promise.all([
      (supabase as any)
        .from("published_menu_sections")
        .select("*, items:published_menu_items(*, menu_item:menu_items(*, category:menu_categories(id,name), prices:menu_item_prices(*)))")
        .eq("menu_id", menu.id)
        .order("sort_order"),
      supabase
        .from("published_menu_packages")
        .select(MENU_PACKAGES_SELECT)
        .eq("menu_id", menu.id)
        .order("sort_order"),
    ]);
    if (se) throwAsServiceError(se);
    // Packages are an extra section, not the page: a failure here must not
    // take the menu down with it (#932).
    if (pe) console.warn("[public-menu] attached packages failed to load", pe);

    return { ...menu, packages: pe ? [] : toMenuPackages(links), sections: (sections || []).map((s: any) => ({
      ...s,
      items: (s.items || []).sort((a: any, b: any) => a.sort_order - b.sort_order),
    })) } as PublicMenu;
  },
});

export function usePublicMenu(slug: string) {
  return useQuery({
    ...publicMenuQuery(slug),
    retry: (failureCount, error) => {
      if (error instanceof NotFoundError) return false;
      return failureCount < 1;
    },
  });
}

// ─── Attached packages (#932) ───
async function fetchMenuPackageIds(menuId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("published_menu_packages")
    .select("package_id")
    .eq("menu_id", menuId)
    .order("sort_order");
  if (error) throw error;
  return (data || []).map((l: { package_id: string }) => l.package_id);
}

// Delete + re-insert, the same pattern sections use. `undefined` leaves the
// menu's attachments alone, so a caller that doesn't know about packages
// can't wipe them.
async function replaceMenuPackages(menuId: string, packageIds: string[] | undefined) {
  if (packageIds === undefined) return;
  const { error: de } = await supabase
    .from("published_menu_packages")
    .delete()
    .eq("menu_id", menuId);
  if (de) throw de;
  if (packageIds.length === 0) return;
  const { error: ie } = await supabase
    .from("published_menu_packages")
    .insert(packageIds.map((package_id, sort_order) => ({ menu_id: menuId, package_id, sort_order })));
  if (ie) throw ie;
}

// ─── Create menu ───
export interface CreateMenuInput {
  title: string;
  slug: string;
  description?: string;
  cover_image_url?: string;
  show_prices?: boolean;
  is_published?: boolean;
  is_listed?: boolean;
  cta_text?: string;
  /** Attached packages in display order (#932). Omit to leave attachments untouched. */
  package_ids?: string[];
  sections: {
    title: string;
    description?: string;
    sort_order: number;
    items: { menu_item_id: string; sort_order: number; price_override?: number; description_override?: string }[];
  }[];
}

export function useCreatePublishedMenu() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ sections, package_ids, ...menuData }: CreateMenuInput) => {
      const { data: menu, error } = await (supabase as any)
        .from("published_menus")
        .insert({ ...menuData, created_by: user?.id, updated_by: user?.id })
        .select()
        .single();
      if (error) throw error;

      for (const sec of sections) {
        const { items, ...secData } = sec;
        const { data: section, error: se } = await (supabase as any)
          .from("published_menu_sections")
          .insert({ ...secData, menu_id: menu.id })
          .select()
          .single();
        if (se) throw se;
        if (items.length > 0) {
          const { error: ie } = await (supabase as any)
            .from("published_menu_items")
            .insert(items.map((i) => ({ ...i, section_id: section.id })));
          if (ie) throw ie;
        }
      }
      await replaceMenuPackages(menu.id, package_ids);
      return menu;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published_menus"] });
      toast.success("Menu created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Update menu ───
export function useUpdatePublishedMenu() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, sections, package_ids, ...menuData }: { id: string } & CreateMenuInput) => {
      const { error } = await (supabase as any)
        .from("published_menus")
        .update({ ...menuData, updated_by: user?.id })
        .eq("id", id);
      if (error) throw error;

      // Delete old sections (cascade deletes items)
      await (supabase as any)
        .from("published_menu_sections")
        .delete()
        .eq("menu_id", id);

      for (const sec of sections) {
        const { items, ...secData } = sec;
        const { data: section, error: se } = await (supabase as any)
          .from("published_menu_sections")
          .insert({ ...secData, menu_id: id })
          .select()
          .single();
        if (se) throw se;
        if (items.length > 0) {
          const { error: ie } = await (supabase as any)
            .from("published_menu_items")
            .insert(items.map((i) => ({ ...i, section_id: section.id })));
          if (ie) throw ie;
        }
      }
      await replaceMenuPackages(id, package_ids);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published_menus"] });
      toast.success("Menu updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Toggle published ───
export function useToggleMenuPublished() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_published }: { id: string; is_published: boolean }) => {
      // Coupling rule: listed requires published. When unpublishing from the list
      // view, also clear is_listed to keep the invariant.
      const patch: { is_published: boolean; is_listed?: boolean } = { is_published };
      if (!is_published) patch.is_listed = false;
      const { error } = await (supabase as any)
        .from("published_menus")
        .update(patch)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published_menus"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Delete menu ───
export function useDeletePublishedMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from("published_menus")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published_menus"] });
      toast.success("Menu deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Duplicate menu ───
export function useDuplicatePublishedMenu() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (sourceId: string) => {
      // Fetch original menu
      const { data: original, error } = await (supabase as any)
        .from("published_menus")
        .select("*")
        .eq("id", sourceId)
        .single();
      if (error) throw error;

      // Insert copy
      const { data: newMenu, error: me } = await (supabase as any)
        .from("published_menus")
        .insert({
          title: original.title + " (Copy)",
          slug: original.slug + "-copy-" + Date.now().toString(36),
          description: original.description,
          cover_image_url: original.cover_image_url,
          show_prices: original.show_prices,
          is_published: false,
          cta_text: original.cta_text,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();
      if (me) throw me;

      // Fetch sections + items
      const { data: sections, error: se } = await (supabase as any)
        .from("published_menu_sections")
        .select("*, items:published_menu_items(*)")
        .eq("menu_id", sourceId)
        .order("sort_order");
      if (se) throw se;

      for (const sec of sections || []) {
        const { data: newSec, error: nse } = await (supabase as any)
          .from("published_menu_sections")
          .insert({ menu_id: newMenu.id, title: sec.title, description: sec.description, sort_order: sec.sort_order })
          .select()
          .single();
        if (nse) throw nse;
        const items = (sec.items || []).map((i: any) => ({
          section_id: newSec.id,
          menu_item_id: i.menu_item_id,
          sort_order: i.sort_order,
          price_override: i.price_override,
          description_override: i.description_override,
        }));
        if (items.length > 0) {
          const { error: ie } = await (supabase as any).from("published_menu_items").insert(items);
          if (ie) throw ie;
        }
      }
      // Degrade, don't throw: the new menu and its sections already exist, and
      // an unpublished copy missing its attachments is harmless (#932).
      try {
        await replaceMenuPackages(newMenu.id, await fetchMenuPackageIds(sourceId));
      } catch (e) {
        console.warn("[duplicate-menu] attached packages not copied", e);
      }
      return newMenu;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["published_menus"] });
      toast.success("Menu duplicated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Submit inquiry ───

export function useSubmitMenuInquiry() {
  return useMutation({
    mutationFn: async (data: {
      menu_id: string;
      package_id?: string;
      name: string;
      email: string;
      phone?: string;
      event_date?: string;
      guest_count?: number;
      message?: string;
      idempotency_key?: string;
      /** The Lead's funnel tag (#925), the page slug. Tracking only: never inserted. */
      content_category?: string;
    }) => {
      // Generate the id client-side so the bridge invocation can reference it
      // without needing an anon SELECT RLS policy on menu_inquiries.
      const id = crypto.randomUUID();
      const { content_category, ...row } = data;
      const { error } = await (supabase as any)
        .from("menu_inquiries")
        .insert({ id, ...row, ...getAttributionForInsert() });
      if (error) {
        // PostgreSQL unique violation — duplicate idempotency_key means
        // this submission was already recorded. Treat as success.
        if (error.code === "23505" && data.idempotency_key) return;
        throw error;
      }
      bridgeLeadToGhl("menu_inquiry", id);
      // Meta Lead (#738), keyed and deduped on the inquiry row id. Independent of
      // the GHL bridge above (#737): a GHL outage must not take the Lead with it.
      trackLeadConversion({
        rowId: id,
        source: "menu_inquiry",
        value: estimateLeadValue(data.guest_count),
        contentCategory: content_category,
      });
    },
    onSuccess: () => {
      toast.success("Inquiry submitted! We'll be in touch soon.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ─── Get inquiries for a menu ───
export function useMenuInquiries(menuId?: string) {
  return useQuery({
    queryKey: ["menu_inquiries", menuId],
    enabled: !!menuId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("menu_inquiries")
        .select("*")
        .eq("menu_id", menuId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });
}
